/* eslint-disable arrow-body-style */
import React, { useRef, useMemo, useState } from 'react';
import { useDebounceFn, useDeepCompareEffect } from 'ahooks';
import { isEmpty, isEqual } from 'lodash-es';
import { Dropdown, Icon, Input } from 'tea-component';
import CheckBoxPanel from './components/CheckBoxPanel';
import useCascadPlacement from './_hooks/useCascadPlacement';
import '@intelligence/components/GenreSelect/index.scss';
import type { CascadeProjectProps } from './types';
import {
  groupCascadeOptionByLevel,
  markCascadeStatus,
  sortArrByStats,
  markCascadeActiveStatus,
  resetCascadeState,
  getSearchLevelDatas,
  getAllIdsByNode,
  setOptionsFirstActive,
} from './utils';
import { isEqualStringArray } from '@gp/utils/commonFun';
import { getCascadeFlatData, getCascadeProjectValues } from '@gp/utils/project';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

/**
 * project级联筛选器
 */
const CascadeProjectCustom: React.FC<CascadeProjectProps> = (props) => {
  const {
    options: optionsProps = [],
    value: valueProps = [],
    onChange: onChangeProps,
    allValues = [],
    btnTitle = '',
    cascadeInfoList = [],
    disabled,
  } = props;

  const { componentNode, placement } = useCascadPlacement();
  const searchInputRef = useRef<HTMLInputElement>(null);
  // 筛选器勾选值
  const [value, setValue] = useState(valueProps);
  const [keywords, setKeywords] = useState('');
  // 是否搜索状态
  const isSearching = useMemo<boolean>(() => keywords?.length > 0, [keywords]);
  // 要维护的两组数据
  // 级联组件实时渲染数据
  const [optionsData, setOptionsData] = useState<any>([]);
  // 级联组件空状态渲染数据
  const [optionsDataNew, setCascadeDataNew] = useState([]);

  useDeepCompareEffect(() => {
    setValue(valueProps);
  }, [valueProps]);

  useDeepCompareEffect(() => {
    setOptionsData(optionsProps);
    setCascadeDataNew(resetCascadeState(optionsProps));
  }, [optionsProps]);

  const handleClose = () => {
    // 关闭组件清空重置筛选关键字
    setKeywords('');
    let resultListTemp: any = [...optionsDataNew];
    resultListTemp = markCascadeStatus(resultListTemp, value);
    resultListTemp = sortArrByStats(resultListTemp);
    const newCascadeData = setOptionsFirstActive(resultListTemp);
    setOptionsData(newCascadeData);
    if (!value?.length) {
      // handOperation(false);
      setValue(allValues);
      onChangeProps?.(allValues);
    } else if (!isEqual(value, valueProps)) {
      onChangeProps?.(value);
    }
  };

  const handleOpen = () => {
    // 关闭组件清空重置筛选关键字
    const resultListTemp = sortArrByStats(optionsData);
    const newCascadeData = setOptionsFirstActive(resultListTemp);
    setOptionsData(newCascadeData);
  };

  // 判断是否需要中间分割线
  const isNeedLine = (length: number, indexNum: number) => {
    if (length < 2 || indexNum >= length - 1) return false;
    return true;
  };

  // 是否是全选
  const isSelectAll = useMemo(() => {
    return isEqualStringArray(value, allValues);
  }, [allValues, value]);

  const { run: debouncedSearchRun } = useDebounceFn(
    (payload: string) => {
      const resultList = getSearchLevelDatas(optionsDataNew, payload) || [];
      let resultListTemp = [...resultList];

      if (isSelectAll && payload !== '') {
        resultListTemp = resetCascadeState(resultList);
        setValue([]);
      } else if (!value?.length && payload === '') {
        setValue(allValues);
      } else if (!isSelectAll && payload === '') {
        let resultListMark: any = [...resultList];
        resultListMark = markCascadeStatus(resultList, value);
        resultListTemp = sortArrByStats(resultListMark);
      }
      const newCascadeData = setOptionsFirstActive(resultListTemp);
      setOptionsData(newCascadeData);
    },
    { wait: 300 },
  );

  const clearInputValue = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    setKeywords('');
    let resultListTemp: any = [...optionsDataNew];
    resultListTemp = markCascadeStatus(resultListTemp, value);
    resultListTemp = sortArrByStats(resultListTemp);
    const newCascadeData = setOptionsFirstActive(resultListTemp);
    setOptionsData(newCascadeData);
  };

  // checkBox点击事件
  const handleCheckBox = (levelItem: any) => {
    const optionsTemp = [...optionsData];
    let valueTemp = [...value];
    if (isSearching && isSelectAll) {
      valueTemp = [];
    }

    // 1.如果state为all,则删除此节点下所有最后项id
    if (levelItem.state === 'all') {
      // 如果是最后一项，则删除本身
      if (levelItem.isEndPanel) {
        valueTemp = value?.filter((item: string) => item !== levelItem.id);
      } else {
        const newIds = getAllIdsByNode(optionsTemp, levelItem);
        const setValue = new Set(newIds);
        valueTemp = value.filter((item: string) => !setValue.has(item));
      }
    }
    // 1.非all状态
    if (levelItem.state === 'none' || levelItem.state === 'partial') {
      // 如果是最后一项，则直接添加
      if (levelItem.isEndPanel) {
        valueTemp.push(levelItem.id);
      } else {
        const newIds = getAllIdsByNode(optionsTemp, levelItem);
        const newValueTemp = [...valueTemp, ...newIds];
        valueTemp = [...new Set(newValueTemp)];
      }
    }

    if (!levelItem.isEndPanel) {
      const resultList = markCascadeActiveStatus(optionsData, levelItem);
      const resultOptionsList = [...resultList];
      setOptionsData(resultOptionsList);
    }
    setValue(valueTemp);
  };

  // item点击事件
  const handleLeveItem = (levelItem: any) => {
    // 如果点击对象是最后一级，则调用点击事件
    if (levelItem?.isEndPanel) {
      handleCheckBox(levelItem);
    } else {
      const resultList = markCascadeActiveStatus(optionsData, levelItem);
      const resultOptionsList = [...resultList];
      setOptionsData(resultOptionsList);
    }
  };

  // clear all/select all点击事件
  const handOperation = (showSelectAll: boolean, activeParent: any = {}) => {
    const optionsTemp = [...optionsData];
    let valueTempNew: any = [];
    let valueTemp = [...value];
    if (isSearching && isSelectAll) {
      valueTemp = [];
    }
    // 无父级，则为第一级，全选
    if (isEmpty(activeParent)) {
      if (!showSelectAll) {
        // 全选
        const allValuesTemp = getCascadeProjectValues(optionsTemp, false);
        valueTempNew = [...new Set(allValuesTemp)];
      } else {
        // clear all
        valueTempNew = [];
      }
    } else {
      if (!showSelectAll) {
        // 全选
        const newIds = getAllIdsByNode(optionsTemp, activeParent);
        const newValueTemp = [...valueTemp, ...newIds];
        valueTempNew = [...new Set(newValueTemp)];
      } else {
        // clear all
        const newIds = getAllIdsByNode(optionsTemp, activeParent);
        const setValue = new Set(newIds);
        valueTempNew = valueTemp?.filter((item: string) => !setValue.has(item));
      }
    }
    setValue(valueTempNew);
  };

  // 整改为此级联筛选器需要的数据结构
  const cascadeDataCur: any[] = useMemo(() => {
    let resultList: any = [...optionsData];
    resultList = markCascadeStatus(optionsData, value);
    const flatDatas = getCascadeFlatData(resultList);
    const flatDatasTemp = [...flatDatas];
    const groupDatas = groupCascadeOptionByLevel(flatDatasTemp);
    return groupDatas;
  }, [optionsData, value]);

  const button = useMemo(() => {
    let btnText = null;
    if (isSelectAll) {
      btnText = 'All';
    } else if (value?.length === 1) {
      const endList = cascadeDataCur[cascadeDataCur?.length - 1]; // [5,6]
      const endItem = endList?.find((item: any) => item.id === value[0]) || {};
      btnText = endItem?.name || '';
    } else {
      btnText = value?.length ? (
        String(value?.length)
      ) : (
        <span className="dbg-common-select-placeholder">All</span>
      );
    }
    return (
      <div className="dropdown-value-container">
        <span className="dropdown-value-container-text">{btnTitle}</span>
        <span className="dropdown-value-result">{btnText}</span>
      </div>
    );
  }, [value, cascadeDataCur, btnTitle, isSelectAll]);

  const renderTreeNodes = (optionsDatas: any[]) => {
    if (!Array.isArray(optionsDatas) || optionsDatas.length === 0) {
      // 搜索无匹配时 cascadeDataCur 为 []，直接 return null 会让整块面板被吞掉，
      // 导致 CheckBoxList 的 isNoData 分支（"No results found"）永远走不到。
      // 这里渲染一个空的 CheckBoxPanel，让其内部 levelListCur 为空时自然触发 isNoData=true。
      if (isSearching) {
        // const firstLevelTitle = cascadeInfoList.find((item) => item.level === 1)?.title || '';
        return (
          <CheckBoxPanel
            onChange={handleCheckBox}
            onClick={handleLeveItem}
            onOperation={handOperation}
            levelList={[]}
            parentList={[]}
            search={keywords}
            title={''}
          />
        );
      }
      return null;
    }
    return optionsDatas.map((levelData: any, index: number) => {
      const parentData = index === 0 ? [] : optionsDatas[index - 1];
      const levelInfo = cascadeInfoList.find(
        (item) => item.level === index + 1,
      );
      const titleValue = levelInfo?.title || '';
      return (
        <React.Fragment key={index}>
          <CheckBoxPanel
            onChange={handleCheckBox}
            onClick={handleLeveItem}
            onOperation={handOperation}
            levelList={levelData}
            parentList={parentData}
            search={keywords}
            title={titleValue}
          />
          {isNeedLine(optionsDatas.length, index) && (
            <div className="panel-divider-line"></div>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div ref={componentNode} className="dbs-cascade-project-filter">
      <Dropdown
        escapeWithReference
        overlayClassName="dbs-cascade-project-filter-overlay dbs-common-box"
        appearance="button"
        trigger="click"
        placement={placement}
        clickClose={false}
        positionFixed={false}
        button={button}
        onClose={handleClose}
        onOpen={handleOpen}
        disabled={disabled}
      >
        <div>
          {/* 搜索输入框 */}
          <div className="dbg-cascade-project-filter-search">
            <Input
              placeholder={'Search'}
              value={keywords}
              ref={searchInputRef}
              className="dbc-region-select-search-input"
              type="text"
              onChange={(val) => {
                setKeywords?.(val);
                debouncedSearchRun(val);
              }}
            />
            <div className="dbg-cascade-project-filter-search-flex">
              {!!searchInputRef?.current?.value && (
                <span
                  className="right-icon-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearInputValue();
                  }}
                >
                  <SvgIcon className="right-icon" type="clear-icon" />
                </span>
              )}
              <Icon className="right-icon-search" type="search" />
            </div>
          </div>
          {/* 级联主体内容*/}
          <div className="intelligence-genre-select-out">
            <div className="intelligence-genre-select-panel">{renderTreeNodes(cascadeDataCur)}</div>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default CascadeProjectCustom;
